import React, { useState, useRef } from "react";
import { useDispatch } from "react-redux";
import moment from "moment";
import { DocumentType } from "src/utils/enum";
import { openDocumentDetail } from "../../../../../../utils/documentModal";
import {
  getName,
  getDocumentLabelWithNumber,
  getFolioDetalle,
} from "./matchRowHelpers";
import { Icon, Popup } from "semantic-ui-react";
import { formatMoneyWithCurrency } from "../../../../../../utils/formatter";
import { importAlertState } from "../../../../../../redux/actions/alert";
import { importOpenSidebar } from "../../../../../../redux/actions/openSidebar";
import { importUpdateTransactions } from "../../../../../../redux/actions/conciliacion";
import { setUpdateDocuments } from "../../../../../../redux/actions/documentostributarios";
import { useSelector } from "react-redux";
import "./StrongCandidate.scss";
import { mergeTransactionMatchsPreserveEntries } from "../../../../../../utils/mergeTransactionMatchs";
import { dispatchRefreshNominaLists } from "src/utils/dispatchRefreshNominaLists";
import ModalNominaImputation, {
  NominaImputationMode,
  getImputableSelections,
} from "../ModalNominaImputation";
import { dt } from "../../../../../../utils/documentsI18n";
import { useModuleTcStatementPicker, isModuleTcObligationMeta } from "../hooks/useModuleTcStatementPicker";
import ModuleTcStatementPickerModal from "./ModuleTcStatementPickerModal";

/**
 * StrongCandidate - Component for displaying strong match candidates
 * Similar to MatchRow but with orange styling and crown icon
 */
const StrongCandidate = ({ 
  candidate, 
  index, 
  totalStrongCandidates,
  matchContainer, 
  editData, 
  history, 
  location,
  generateMatch,
  generateMatchFetching,
  readOnly = false
}) => {
  const dispatch = useDispatch();
  const { openSidebar } = useSelector((state) => state.openSidebar);
  const [isHovered, setIsHovered] = useState(false);
  const [isReconciling, setIsReconciling] = useState(false);
  const [showNominaModal, setShowNominaModal] = useState(false);
  const [pendingMatchInput, setPendingMatchInput] = useState(null);
  const reconcileClickedRef = useRef(false);
  const companyId = editData?.companyId || candidate?.companyId;
  const tcPicker = useModuleTcStatementPicker(companyId);

  // Use helper functions to extract match data
  const name = getName(candidate);
  const documentLabelWithNumber = getDocumentLabelWithNumber(candidate);
  const folioDetalle = getFolioDetalle(candidate);

  // Handler to open document detail modal
  const handleDocumentClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (candidate.id && [DocumentType.TAX_DOCUMENT, DocumentType.OBLIGATION, DocumentType.CREDIT].includes(candidate.documentType)) {
      openDocumentDetail(candidate.id, history, location);
    }
  };

  // Get match amount and currency
  // PRIORITY: Use remaining first (available amount for matching), then fallback to amount
  const matchKey = candidate.id || candidate.docId || candidate.settleId;
  const amount = matchContainer[matchKey + "matchedAmount"] || matchContainer[matchKey] || candidate.meta?.remaining || candidate.remaining || candidate.meta?.amount || candidate.amount || 0;
  const currency = matchContainer[matchKey + "currency"] || candidate.currencyType || candidate.currency || 'CLP';
  const matchDate = moment(candidate.documentDate || candidate.date);
  
  const isClickable = [DocumentType.TAX_DOCUMENT, DocumentType.OBLIGATION, DocumentType.CREDIT].includes(candidate.documentType);
  
  // Check if any candidate is currently reconciling (global state) or if readOnly mode
  const isDisabled = isReconciling || generateMatchFetching || readOnly;

  const strongCandidateLabel = totalStrongCandidates === 1 ? 'Candidato Fuerte Único' : 'Candidato Fuerte';

  // Generate avatar initials
  const getAvatarInitials = () => {
    if (documentLabelWithNumber) {
      const words = documentLabelWithNumber.split(' ');
      if (words.length >= 2) {
        return (words[0][0] + words[1][0]).toUpperCase();
      }
      return documentLabelWithNumber.substring(0, 2).toUpperCase();
    }
    return 'SC'; // Default: Strong Candidate
  };

  // Format amount with parentheses for red/received amounts
  const formatAmount = (amount, currency, isReceived) => {
    const formatted = formatMoneyWithCurrency(Math.abs(amount), currency);
    // If it's a received/red amount, wrap in parentheses
    if (isReceived) {
      return `(${formatted})`;
    }
    return formatted;
  };

  const executeMatchRequest = async (input) => {
    setIsReconciling(true);
    try {
      const { data: response, error } = await generateMatch({ input });

      if (error) {
        const errorMessage = error.graphQLErrors?.[0]?.message || error.message || "Hubo un problema al conciliar el documento.";
        const isPeriodLocked = errorMessage && typeof errorMessage === 'string' && 
          errorMessage.toLowerCase().includes('período') && 
          errorMessage.toLowerCase().includes('cerrado');
        dispatch(
          importAlertState({
            concept: isPeriodLocked ? "Período Cerrado" : undefined,
            content: errorMessage,
            state: isPeriodLocked ? "warning" : "error",
            duration: isPeriodLocked ? 6 : 4,
          })
        );
        return;
      }

      // Update Redux with new transaction data
      if (response?.generateMatch?.data) {
        const type = response.generateMatch.data["__typename"];

        if (type === "Transaction") {
          const { remaining, matchs } = response.generateMatch.data;
          const newEditData = {
            ...editData,
            ...{
              remaining,
              matchs: mergeTransactionMatchsPreserveEntries(editData.matchs, matchs),
            },
          };

          dispatch(
            importOpenSidebar({
              ...openSidebar,
              editData: newEditData,
            })
          );
        }

        // Trigger refetch to update all data
        dispatch(importUpdateTransactions(true));
        if (editData.documentType) {
          dispatch(setUpdateDocuments({ value: true }));
        }
        if (input?.nominaId || input?.selectionId || input?.imputationMode) {
          dispatchRefreshNominaLists(dispatch);
        }
      }
    } catch (error) {
      const errorMessage = error?.message || "Hubo un problema al conciliar el documento.";
      const isPeriodLocked = errorMessage && typeof errorMessage === 'string' && 
        errorMessage.toLowerCase().includes('período') && 
        errorMessage.toLowerCase().includes('cerrado');
      dispatch(
        importAlertState({
          concept: isPeriodLocked ? "Período Cerrado" : undefined,
          content: errorMessage,
          state: isPeriodLocked ? "warning" : "error",
          duration: isPeriodLocked ? 6 : 4,
        })
      );
    } finally {
      setIsReconciling(false);
      reconcileClickedRef.current = false;
    }
  };

  const executeMatch = async (input) => {
    const obligationMeta = [candidate?.meta, editData?.meta].find(isModuleTcObligationMeta);
    const { input: next, prompted } = await tcPicker.attachOrPrompt(
      input,
      obligationMeta
    );
    if (prompted) {
      setIsReconciling(false);
      reconcileClickedRef.current = false;
      return;
    }
    return executeMatchRequest(next);
  };

  const handleModuleTcPickerConfirm = async (statementId) => {
    const next = tcPicker.confirm(statementId);
    if (next) await executeMatchRequest(next);
  };

  const handleNominaConfirm = ({ imputationMode, nominaId, selectionId }) => {
    setShowNominaModal(false);
    if (!pendingMatchInput) return;
    const enrichedInput = {
      ...pendingMatchInput,
      imputationMode: imputationMode || NominaImputationMode.NEW_NOMINA,
    };
    if (nominaId) enrichedInput.nominaId = nominaId;
    if (selectionId) enrichedInput.selectionId = selectionId;
    setPendingMatchInput(null);
    executeMatch(enrichedInput);
  };

  // Handler for reconcile/match
  const handleReconcile = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (reconcileClickedRef.current || isDisabled || !editData || !candidate) return;
    reconcileClickedRef.current = true;

    setIsReconciling(true);

    try {
      // Transaction is origin, document is end
      // IMPORTANT: Use the minimum between transaction remaining and document remaining
      // This ensures we don't try to match more than what's available in the transaction
      // Use Math.abs to handle negative values (for RECEIVED transactions)
      // For documents, remaining is in meta.remaining
      const editDataIsDoc = !!editData.documentType;
      const editDataRemaining = editDataIsDoc
        ? Math.abs(editData.meta?.remaining !== undefined && editData.meta?.remaining !== null ? editData.meta.remaining : (editData.amount || 0))
        : Math.abs(editData.remaining !== undefined && editData.remaining !== null ? editData.remaining : 0);
      const candidateRemaining = Math.abs(candidate.meta?.remaining !== undefined && candidate.meta?.remaining !== null 
        ? candidate.meta.remaining 
        : (candidate.remaining !== undefined && candidate.remaining !== null ? candidate.remaining : 0));
      
      // Validate that we have valid remaining amounts
      if (editDataRemaining <= 0) {
        dispatch(
          importAlertState({
            content: editDataIsDoc ? `El documento no tiene monto disponible para conciliar.` : `La transacción no tiene monto disponible para conciliar.`,
            state: "error",
            duration: 4,
          })
        );
        setIsReconciling(false);
        return;
      }
      
      if (candidateRemaining <= 0) {
        dispatch(
          importAlertState({
            content: `El documento candidato no tiene monto disponible para conciliar.`,
            state: "error",
            duration: 4,
          })
        );
        setIsReconciling(false);
        return;
      }
      
      // Rename for clarity in match amount calculation
      const transactionRemaining = editDataRemaining;
      const documentRemaining = candidateRemaining;
      
      // Use the minimum between transaction remaining and document remaining
      const matchAmount = Math.min(transactionRemaining, documentRemaining);
      
      // Determine origin/end types based on editData type
      // If editData is a document (has documentType), the match is OBLIGATION ↔ TAX_DOCUMENT
      // If editData is a transaction, the match is TRANSACTION ↔ DOCUMENT
      const editDataIsDocument = !!editData.documentType;
      const editDataIsRendicion = editDataIsDocument && editData.documentType === 'OBLIGATION';
      
      let input;
      if (editDataIsRendicion) {
        // Obligaciones sidebar: editData es Rendición (OBLIGATION), candidate es Factura (TAX_DOCUMENT)
        input = {
          originType: "OBLIGATION",
          originId: editData._id || editData.id,
          endType: candidate.documentType || "TAX_DOCUMENT",
          endId: candidate._id || candidate.id || candidate.docId,
          amount: matchAmount,
          originalAmount: matchAmount,
          accountPlan: "NOPLAN",
          companyId: editData.companyId || candidate.companyId,
          paymentDate: candidate.documentDate || editData.documentDate || new Date(),
          currency: currency || 'CLP',
          obligationType: editData.meta?.typeLabel || editData.meta?.type || '',
          matchSource: 'CONFIRMED_STRONG_CANDIDATE',
        };
      } else if (editDataIsDocument) {
        // Document sidebar: candidate es Rendición (OBLIGATION), editData es TAX_DOCUMENT
        input = {
          originType: candidate.documentType || "OBLIGATION",
          originId: candidate.id || candidate.docId || candidate._id,
          endType: editData.documentType || "TAX_DOCUMENT",
          endId: editData._id || editData.id,
          amount: matchAmount,
          originalAmount: matchAmount,
          accountPlan: "NOPLAN",
          companyId: editData.companyId || candidate.companyId,
          paymentDate: editData.documentDate || new Date(),
          currency: currency || 'CLP',
          obligationType: candidate.meta?.typeLabel || candidate.meta?.type || '',
          matchSource: 'CONFIRMED_STRONG_CANDIDATE',
        };
      } else {
        // Transaction sidebar: candidate es documento, editData es TRANSACTION
        input = {
          originType: "TRANSACTION",
          originId: editData.id || editData._id,
          endType: candidate.documentType || "TAX_DOCUMENT",
          endId: candidate.id || candidate.docId,
          amount: matchAmount,
          originalAmount: matchAmount,
          accountPlan: "NOPLAN",
          companyId: editData.companyId || candidate.companyId,
          paymentDate: editData.date || new Date(),
          currency: currency || 'CLP',
          obligationType: candidate.meta?.typeLabel || '',
          matchSource: 'CONFIRMED_STRONG_CANDIDATE',
        };
      }

      // IMPORTANT: Remove 'origin' field if it exists - it's not part of GenerateMatchInput schema
      delete input.origin;

      // Pre-match nomina check: if the document has active nomina
      // selections, ask the user before applying the match.
      const totalUsed = Number(candidate?.meta?.nominaStatus?.totalUsed ?? 0)
      const imputableSelections = getImputableSelections(candidate?.meta?.nominaStatus)
      if (totalUsed > 0 && imputableSelections.length > 0) {
        setPendingMatchInput(input)
        setShowNominaModal(true)
        setIsReconciling(false)
        reconcileClickedRef.current = false
        return
      }

      await executeMatch(input)
    } catch (error) {
      const errorMessage = error?.message || "Hubo un problema al conciliar el documento.";
      const isPeriodLocked = errorMessage && typeof errorMessage === 'string' && 
        errorMessage.toLowerCase().includes('período') && 
        errorMessage.toLowerCase().includes('cerrado');
      dispatch(
        importAlertState({
          concept: isPeriodLocked ? "Período Cerrado" : undefined,
          content: errorMessage,
          state: isPeriodLocked ? "warning" : "error",
          duration: isPeriodLocked ? 6 : 4,
        })
      );
      setIsReconciling(false);
      reconcileClickedRef.current = false;
    }
  };

  return (
    <>
      <div className={`strong-candidate-container ${isDisabled ? 'strong-candidate-container--disabled' : ''}`}>
        <div className="strong-candidate-label">
          {strongCandidateLabel}
          <Popup
            trigger={
              <Icon
                name="info circle"
                size="small"
                className="strong-candidate-label-icon"
              />
            }
            content="El monto y el emisor/receptor coinciden al 100% con el movimiento"
            inverted
            position="top center"
            size="tiny"
          />
        </div>
        <div 
          className="strong-candidate-item"
          onMouseEnter={() => !isDisabled && setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onClick={handleReconcile}
        >
          <div className="strong-candidate-avatar">
            {getAvatarInitials()}
          </div>
          <div className="strong-candidate-body">
            <div className="strong-candidate-header">
              <div className={`strong-candidate-user ${isClickable ? 'strong-candidate-user--clickable' : ''}`}>
                {isClickable ? (
                  <span onClick={handleDocumentClick}>
                    {documentLabelWithNumber}
                  </span>
                ) : (
                  documentLabelWithNumber
                )}
                {folioDetalle}
              </div>
              <div className="strong-candidate-time-container">
                <div className="strong-candidate-time">
                  {dt('reconcile.issuedOn')} {matchDate.format('DD/MM/YY')}
                </div>
                {isReconciling ? (
                  <div className="strong-candidate-reconcile-hint strong-candidate-reconcile-hint--spinner">
                    <div className="strong-candidate-small-spinner"></div>
                  </div>
                ) : isHovered && !isDisabled && (
                  <div className="strong-candidate-reconcile-hint">
                    <span className="strong-candidate-reconcile-text">{dt('reconcile.reconcile')}</span>
                    <Icon
                      name="arrow up right"
                      className="strong-candidate-reconcile-icon"
                      style={{ transform: 'rotate(45deg)' }}
                    />
                  </div>
                )}
              </div>
              <div className={`strong-candidate-amount-container ${candidate.meta?.isReceived ? 'strong-candidate-amount-container--received' : 'strong-candidate-amount-container--given'}`}>
                <span className="strong-candidate-amount-text">
                  {formatAmount(amount, currency, candidate.meta?.isReceived)}
                </span>
              </div>
            </div>
            <div className="strong-candidate-text">
              {name}
            </div>
          </div>
        </div>
      </div>
      <ModalNominaImputation
        open={showNominaModal}
        onClose={() => {
          setShowNominaModal(false)
          setPendingMatchInput(null)
          reconcileClickedRef.current = false
        }}
        onConfirm={handleNominaConfirm}
        document={candidate}
        conflictAmount={pendingMatchInput?.amount ?? 0}
      />
      <ModuleTcStatementPickerModal
        open={tcPicker.open}
        participants={tcPicker.participants}
        onCancel={() => {
          tcPicker.cancel();
          setIsReconciling(false);
          reconcileClickedRef.current = false;
        }}
        onConfirm={handleModuleTcPickerConfirm}
      />
    </>
  );
};

export default StrongCandidate;

